/**
 * EIN (Employer Identification Number) formatting utilities
 * EIN format: XX-XXXXXXX (2 digits, hyphen, 7 digits)
 */

const EIN_LENGTH = 9; // Total digits in an EIN

function removeNonDigits(rawValue: string): string {
  return rawValue.replace(/\D/g, "");
}

/**
 * Formats a raw EIN input value while the user types. The logic keeps the
 * number to exactly 9 digits and applies the standard XX-XXXXXXX format.
 *
 * @param rawValue - The raw input string from the user
 * @returns Formatted EIN string (e.g., "12-3456789")
 */
export function formatEINInputValue(rawValue: string): string {
  const digitsOnly = removeNonDigits(rawValue).slice(0, EIN_LENGTH);

  if (digitsOnly.length === 0) {
    return "";
  }

  if (digitsOnly.length <= 2) {
    return digitsOnly;
  }

  // Format: XX-XXXXXXX
  const prefix = digitsOnly.slice(0, 2);
  const suffix = digitsOnly.slice(2);

  return `${prefix}-${suffix}`;
}

/**
 * Removes any non-digit characters from an EIN value. Useful before sending
 * the number to an API that expects only digits.
 *
 * @param rawValue - The formatted or unformatted EIN string
 * @returns EIN with only digits
 */
export function stripEINFormatting(rawValue: string): string {
  return removeNonDigits(rawValue);
}

/**
 * Validates if an EIN has the correct format and length
 *
 * @param ein - The EIN string to validate
 * @returns true if valid, false otherwise
 */
export function isValidEIN(ein: string): boolean {
  const digitsOnly = removeNonDigits(ein);
  return digitsOnly.length === EIN_LENGTH;
}

/**
 * Formats an EIN for display purposes. Useful for showing stored EIN values
 * in a consistent XX-XXXXXXX format throughout the application.
 *
 * @param ein - The EIN string (formatted or unformatted)
 * @returns Formatted EIN string (e.g., "12-3456789") or original value if invalid
 */
export function formatEINForDisplay(ein: string | null | undefined): string {
  if (!ein) return "";

  const digitsOnly = removeNonDigits(ein);

  // If not enough digits, return as-is (might be partial or invalid)
  if (digitsOnly.length < 2) {
    return digitsOnly;
  }

  // Format: XX-XXXXXXX
  const prefix = digitsOnly.slice(0, 2);
  const suffix = digitsOnly.slice(2);

  return suffix ? `${prefix}-${suffix}` : prefix;
}
